/**
 * On-screen controls for touch devices (docs/spec/07 §3): a d-pad bottom left, GRAB bottom right,
 * pause top right. DOM, because several fingers at once are far simpler here than on the canvas.
 */

import type { Dir } from '../sim/types';
import { DIR_DOWN, DIR_LEFT, DIR_RIGHT, DIR_UP } from '../sim/types';
import type { MenuAction } from '../ui/scene';
import type { DirectionLatch } from './latch';

export function isTouchDevice(): boolean {
  try {
    return window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window;
  } catch {
    return false;
  }
}

export class TouchControls {
  private readonly root: HTMLElement;
  private readonly pad: HTMLElement;
  private readonly knob: HTMLElement;
  private padPointer: number | null = null;

  constructor(
    private readonly latch: DirectionLatch,
    private readonly onAction: (a: MenuAction) => void,
    private readonly onAny: () => void,
  ) {
    this.root = document.createElement('div');
    this.root.id = 'touch';
    this.root.hidden = true;
    this.pad = document.createElement('div');
    this.pad.className = 'pad';
    this.knob = document.createElement('div');
    this.knob.className = 'knob';
    this.pad.append(this.knob);
    const grab = this.button('grab', 'GRAB');
    const pause = this.button('pause', 'II');
    this.root.append(this.pad, grab, pause);
    document.body.append(this.root);

    this.pad.addEventListener('pointerdown', (e) => this.padMove(e, true));
    this.pad.addEventListener('pointermove', (e) => this.padMove(e, false));
    for (const t of ['pointerup', 'pointercancel', 'lostpointercapture'] as const) {
      this.pad.addEventListener(t, (e) => this.padEnd(e));
    }
    grab.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.onAny();
      this.latch.fireDown('t:grab');
      grab.classList.add('on');
    });
    for (const t of ['pointerup', 'pointercancel', 'pointerleave'] as const) {
      grab.addEventListener(t, () => {
        this.latch.fireUp('t:grab');
        grab.classList.remove('on');
      });
    }
    pause.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.onAny();
      this.onAction('pause');
    });
  }

  private button(cls: string, label: string): HTMLButtonElement {
    const b = document.createElement('button');
    b.className = cls;
    b.textContent = label;
    b.setAttribute('aria-label', cls);
    return b;
  }

  setVisible(visible: boolean): void {
    if (this.root.hidden === !visible) return;
    this.root.hidden = !visible;
    if (!visible) {
      this.latch.release('t:pad');
      this.latch.fireUp('t:grab');
    }
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  private padMove(e: PointerEvent, start: boolean): void {
    if (start) {
      e.preventDefault();
      this.onAny();
      this.padPointer = e.pointerId;
      this.pad.setPointerCapture(e.pointerId);
    }
    if (this.padPointer !== e.pointerId) return;
    const r = this.pad.getBoundingClientRect();
    const dx = e.clientX - (r.left + r.width / 2);
    const dy = e.clientY - (r.top + r.height / 2);
    const dead = r.width * 0.12;
    let dir: Dir | null = null;
    if (Math.max(Math.abs(dx), Math.abs(dy)) > dead) {
      dir = Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? DIR_RIGHT : DIR_LEFT) : dy > 0 ? DIR_DOWN : DIR_UP;
    }
    const lim = r.width * 0.3;
    const len = Math.max(1, Math.hypot(dx, dy));
    const k = Math.min(1, lim / len);
    this.knob.style.transform = `translate(${dx * k}px, ${dy * k}px)`;
    if (dir === null) this.latch.release('t:pad');
    else if (this.latch.current() !== dir) this.latch.press('t:pad', dir);
  }

  private padEnd(e: PointerEvent): void {
    if (this.padPointer !== e.pointerId) return;
    this.padPointer = null;
    this.latch.release('t:pad');
    this.knob.style.transform = '';
  }
}
